/**
 * Sticky decision-context strip — water · crop · salts · V · CF.
 * Signal only; no essays.
 */
import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { useAppSelector } from '../../hooks/reduxHooks';

export interface DssContextStripProps {
  /** Local overrides (Recipe stage params not yet in Redux) */
  stockVolumeL?: number;
  volumeUnit?: string;
  concentrationFactor?: number;
  degreeOfFreedom?: string;
  compact?: boolean;
}

const DssContextStrip: React.FC<DssContextStripProps> = ({
  stockVolumeL,
  volumeUnit = 'L',
  concentrationFactor,
  degreeOfFreedom,
  compact = true,
}) => {
  const { waterProfile, formulation, saltSelection, stockSolution } =
    useAppSelector((s) => s.workflow);

  const vol =
    stockVolumeL ??
    (stockSolution?.volume != null ? Number(stockSolution.volume) : undefined);
  const unit = volumeUnit || stockSolution?.volumeUnit || 'L';
  const cf =
    concentrationFactor ??
    (stockSolution?.dilutionRatio != null
      ? Number(stockSolution.dilutionRatio)
      : undefined);
  const saltN = saltSelection?.selectedSalts?.length ?? 0;

  const bits: { key: string; label: string; ok: boolean }[] = [
    {
      key: 'water',
      label: waterProfile?.name
        ? `Water: ${waterProfile.name}`
        : 'Water: —',
      ok: Boolean(waterProfile),
    },
    {
      key: 'crop',
      label: formulation?.name
        ? `Crop: ${formulation.name}`
        : 'Crop: —',
      ok: Boolean(formulation),
    },
    {
      key: 'salts',
      label: saltN > 0 ? `Salts: ${saltN}` : 'Salts: —',
      ok: saltN > 0,
    },
  ];

  if (vol != null && vol > 0) {
    bits.push({
      key: 'vol',
      label: `V: ${vol} ${unit}`,
      ok: true,
    });
  }
  if (cf != null && cf >= 1) {
    bits.push({
      key: 'cf',
      label: `CF: ${cf}`,
      ok: true,
    });
  }
  if (degreeOfFreedom) {
    bits.push({
      key: 'dof',
      label: `DOF: ${degreeOfFreedom}`,
      ok: true,
    });
  }

  return (
    <Box
      component="aside"
      aria-label="Calculation context"
      sx={{
        position: 'sticky',
        top: 56,
        zIndex: 10,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.75,
        py: 1,
        px: { xs: 1, sm: 1.5 },
        mb: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: '0 1px 0 rgba(0,0,0,0.04)',
      }}
    >
      {!compact && (
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          Context
        </Typography>
      )}
      {bits.map((b) => (
        <Chip
          key={b.key}
          size="small"
          label={b.label}
          variant="outlined"
          color={b.ok ? 'default' : 'warning'}
          sx={{
            height: 26,
            fontWeight: 500,
            borderColor: b.ok ? 'divider' : 'warning.main',
          }}
        />
      ))}
    </Box>
  );
};

export default DssContextStrip;
